import React, { useEffect, useMemo, useState } from "react";
import { AssortGroup } from "../../../models/AssortGroup";
import useTicketsByAssortGroup from "./hooks/useTicketsByAssortGroup";
import "./style.scss";
import "../../../../style/block.scss";
import "../../../../style/buttons.scss";
import "../../../../style/feature.scss";
import { IoChevronDown, IoChevronUp } from "react-icons/io5";
import TicketLineSummary from "../../Universal/TicketLineSummary";
import PreBasketSummary from "../../Universal/PreBasketSummary";
import usePreBasket from "../../Universal/hooks/usePreBasket";
import useBasketManager from "../../FinalizePage/hooks/useBasketManager";
import { useTranslation } from "react-i18next";
import EAProductBanner from "../../shared/ProductBanner/EAProductBanner";
import { applyMainPriceTemplate } from "../../shared/ProductBanner/parseBannerPrice";
import useDesktopSize from "../../../../utils/hooks/useDesktopSize";
import getId from "../../../../utils/getId";
import useGetLowestPrice from "../../../../utils/hooks/useGetLowestPrice";
import { SaleStep } from "../../../store/stepSaleConfig/stepSaleConfigReducer";
import PassLineSummary from "../../Universal/PassLineSummary";
import { getCheapestConfidentMonthlyRate, getMonthPassPrice } from "../../Universal/PassLineSummary/passMonthlyRate";

interface IProps {
  item: AssortGroup;
  saleStep?: SaleStep;
  sortType: "assortGroup" | "colorGroup" | "shapeGroup" | "colorGroupPasses" | "allParams";
  allParams?: any;
  highlightBanner?: boolean;
  bannerLabel?: string;
  customMainPrice?: string;
  crossedPrice?: string;
  additionalPriceInfo?: string;
  highlightedFeatures?: string[];
  passSaleVariant?: boolean;
  buttonCustomTitle?: string;
}

const LimitedTicketBlock: React.FC<IProps> = ({
  item,
  saleStep,
  sortType,
  allParams = {},
  highlightBanner,
  bannerLabel,
  customMainPrice,
  crossedPrice,
  additionalPriceInfo,
  highlightedFeatures,
  passSaleVariant,
  buttonCustomTitle,
}) => {
  const { t } = useTranslation();
  const {
    requestGetTicketsByAssortGroup,
    requestGetTicketsByColor,
    requestGetTicketsByShape,
    requestGetTicketsByAllParams,
    requestGetPassesByShape,
    requestGetPassesByColor,
    passesAndAssorts,
  } = useTicketsByAssortGroup();
  const [showTickets, setShowTicket] = useState(false);
  const isDesktop = useDesktopSize();
  const { preBasket, addItemToBasket, removeItemFromBasket, clearBasket, setItemQuantity } = usePreBasket();
  const { getBasket } = useBasketManager();
  const handleDeleteBasket = () => {
    clearBasket();
    setTimeout(() => {
      getBasket();
    }, 200);
  };

  useEffect(() => {
    if (sortType === "assortGroup") {
      requestGetTicketsByAssortGroup(item.objectDb, item.objectId);
    }
    if (sortType === "colorGroup") {
      requestGetTicketsByColor(item.objectDb, item.objectId);
      requestGetPassesByColor(getId(item));
    }
    if (sortType === "shapeGroup") {
      requestGetTicketsByShape(item.objectDb, item.objectId);
      requestGetPassesByShape(getId(item));
    }
    // if (sortType === "colorGroupPasses") {
    //   requestGetPassesByColor(getId(item));
    // }
    if (sortType === "allParams") {
      requestGetTicketsByAllParams(allParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.objectId]);

  const getButtonTitle = () => {
    if (showTickets) {
      return t("schowaj_bilety");
    }
    return t(passSaleVariant ? "pokaz_karnety" : "pokaz_bilety");
  };

  const handleOpenExpanded = () => {
    if (showTickets) {
      setShowTicket(false);
    } else {
      setShowTicket(true);
    }
  };

  const hasCustomPriceCaption = Boolean(customMainPrice?.trim());
  const monthlyComparisonEnabled = passSaleVariant === true && saleStep?.showMonthlyPriceComparison === true;
  const lowestPrice = useGetLowestPrice(passesAndAssorts, isDesktop, !hasCustomPriceCaption);
  const monthPassPrice = useMemo(
    () => (monthlyComparisonEnabled ? getMonthPassPrice(passesAndAssorts) : null),
    [monthlyComparisonEnabled, passesAndAssorts],
  );
  const cheapestMonthlyRate = useMemo(
    () =>
      monthlyComparisonEnabled && !hasCustomPriceCaption
        ? getCheapestConfidentMonthlyRate(passesAndAssorts)
        : null,
    [monthlyComparisonEnabled, hasCustomPriceCaption, passesAndAssorts],
  );

  const formatPriceAmount = (value: number) => {
    const rounded = Math.round(value * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  };

  const bannerPrice = hasCustomPriceCaption
    ? applyMainPriceTemplate(customMainPrice, lowestPrice)
    : cheapestMonthlyRate != null
      ? `${isDesktop ? t("ceny_od") : t("od")} ${formatPriceAmount(cheapestMonthlyRate)} ${t("zl")} ${t("za_miesiac_opis")}`
      : lowestPrice;

  return (
    <>
      <EAProductBanner
        title={item.name}
        pictureUrl={item.pictureUrl}
        description={item.description}
        mainPrice={bannerPrice}
        configuredPrice={hasCustomPriceCaption}
        crossedPrice={crossedPrice ? crossedPrice.replace("{{price}}", lowestPrice) : ""}
        buttonTitle={buttonCustomTitle ? buttonCustomTitle : getButtonTitle()}
        buttonEndIcon={showTickets ? <IoChevronUp /> : <IoChevronDown />}
        buttonVariant={showTickets ? "outlined" : "filled"}
        expanded={showTickets}
        onClick={handleOpenExpanded}
        highlightBanner={highlightBanner}
        bannerLabel={bannerLabel}
        additionalPriceInfo={additionalPriceInfo}
        highlightedFeatures={highlightedFeatures}
        loginRequired={saleStep?.loginRequired}
      >
        <div className="single-event-tickets">
          <div className="head-title flex-between-center">
            <b className="head-title__label">{t(passSaleVariant ? "wybierz_karnet" : "wybierz_bilety")}</b>
          </div>
          {passSaleVariant === true ? (
            <div className="tickets-list-group">
              {passesAndAssorts.map((itemTicket, index) => (
                <div className="ticket-line-container" key={index}>
                  <PassLineSummary
                    item={itemTicket}
                    preBasket={preBasket}
                    addItemToBasket={addItemToBasket}
                    removeItemFromBasket={removeItemFromBasket}
                    monthPassPrice={monthPassPrice}
                    showMonthlyComparison={monthlyComparisonEnabled}
                  />
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="tickets-list-group">
                {passesAndAssorts.map((itemTicket, index) => (
                  <div className="ticket-line-container" key={index}>
                    <TicketLineSummary
                      item={itemTicket}
                      preBasket={preBasket}
                      addItemToBasket={addItemToBasket}
                      removeItemFromBasket={removeItemFromBasket}
                      setItemQuantity={setItemQuantity}
                      allowManualInput={true}
                    />
                  </div>
                ))}
              </div>
              <PreBasketSummary preBasket={preBasket} clearBasket={handleDeleteBasket} />
            </>
          )}
        </div>
      </EAProductBanner>
    </>
  );
};

export default LimitedTicketBlock;
